import React, { FC, useEffect, useMemo } from "react";
import Modal from "@/components/Modal/index";
import { useAppTheme } from "@/utils/theme";
import Box from "@mui/material/Box";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useTranslation } from "react-i18next";
import Button from "@/components/Button";
import { useFeedBack } from "@/providers/FeedBackProvider/FeedBackContext";
import { ErrorResponse } from "@/domain/error.interface";
import { ACTION_CODE, MODULE_NAME, OPTIONS } from "@/domain/auditlog.enum";
import { useUserStore } from "@/hooks/useUserStore";
import { useCreateAuditlog } from "@/hooks/useAuditLogs";
import { RoleStatusEnum } from "@/domain/role.enum";
import Grid from "@mui/material/Grid2";
import { TextField, Skeleton } from "@mui/material";
import Select from "@/components/Select/index";
import {
  useGetProgramOrgUnits,
  useParticipantById,
} from "@/hooks/useParticipants";
import { useParams } from "react-router-dom";
import { useGetCmCoordinator } from "@/hooks/useUsers";
import { useGetProgram } from "@/hooks/management/usePrograms.manage";
import { Enrollment } from "@/domain/enrollment.interface";
import { PermissionDeniedModal } from "@/components/PermissionDeniedModal/PermissionDeniedModal";
import {
  enrollProgramValidationSchema,
  EnrollProgramFormData,
} from "../../../schemas/participants/validation.schema";
import { useUpdateEnrollment } from "@/hooks/useEnrollments";
import { AssignCMCoordinatorEnum } from "@/domain/programs.enum";

type ParticipantEditProgramModalProps = {
  open: boolean;
  onClose: () => void;
  enrollment?: Enrollment;
};

const ParticipantEditProgramModal: FC<ParticipantEditProgramModalProps> = ({
  open,
  onClose,
  enrollment,
}) => {
  const { t } = useTranslation("clients");
  const { t: tCommon } = useTranslation("common");
  const { userInfo, userHasAccessToCode } = useUserStore();
  const { showSnackBar } = useFeedBack();
  const theme = useAppTheme();
  const { participantId } = useParams();

  const createAuditLog = useCreateAuditlog();
  const updateEnrollment = useUpdateEnrollment();

  // AC00-B: Check permissions
  const canEditEnrollment = userHasAccessToCode("PARTICIPANT_EDIT_ENROLLMENT");

  // AC00-A: Audit log when opening modal
  useEffect(() => {
    if (open && canEditEnrollment && enrollment) {
      createAuditLog.mutate({
        appType: "WEB_BACK_OFFICE",
        module: MODULE_NAME.ENROLLMENTS,
        option: OPTIONS.ENROLLMENT,
        actionCode: ACTION_CODE.WEB_PART_ENROLL_EDIT_ENTER,
        action: "When entering to edit",
        detail: "User has entered the dialog to edit enrollment",
        transactionDate: new Date(),
        accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
        createdBy: userInfo?._id || "",
        name: `${userInfo?.firstName} ${userInfo?.lastName}`,
      });
    }
  }, [open]);

  const defaultValues = useMemo(() => {
    if (!enrollment)
      return {
        name: "",
        status: RoleStatusEnum.ACTIVE,
        actions: [],
      };

    return {
      program: enrollment.programId || "",
      organizationUnit: enrollment.orgUnitId || "",
      cmCoordinator: enrollment.cmCoordinator || "",
      formFactor1: enrollment.formFactor1 || "",
      formFactor2: enrollment.formFactor2 || "",
      naloxoneId: enrollment.naloxoneId || "",
    };
  }, [enrollment]);

  // Get program ID from enrollment to load program data early (before useForm)
  const initialProgramId = enrollment?.programId || "";

  const { data: participantResponse } = useParticipantById(participantId || "");

  const { programOrgUnits, isFetchingProgramOrgUnits } = useGetProgramOrgUnits(
    participantId || "",
    userInfo?._id || "",
    true,
  );

  // Load program data early using enrollment's programId
  const { data: initialProgram, isFetchingProgram } = useGetProgram(
    initialProgramId || "",
  );

  // Create resolver with useMemo to update when program data is available
  // Similar to defaultValues, this will update when program changes
  const validationResolver = useMemo(
    () =>
      yupResolver(
        enrollProgramValidationSchema(
          tCommon,
          (initialProgram?.configuration as any)?.assignCMCoordinator,
          (initialProgram?.configuration as any)?.enabledFormFactor2,
        ),
      ),
    [
      tCommon,
      (initialProgram?.configuration as any)?.assignCMCoordinator,
      (initialProgram?.configuration as any)?.enabledFormFactor2,
    ],
  );

  const {
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors },
    trigger,
    getValues,
  } = useForm<EnrollProgramFormData>({
    resolver: validationResolver,
    mode: "all",
    defaultValues: defaultValues,
  });

  const programWatched = watch("program") as string;
  const organizationUnitWatched = watch("organizationUnit") as string;
  const formFactor1Watched = watch("formFactor1") as string;

  // Load program data when programWatched changes (in case user changes program)
  const { data: program, isFetchingProgram: isFetchingProgramFromWatch } =
    useGetProgram(programWatched || "");

  // Use program from watch if available, otherwise use initial program
  const currentProgram = program || initialProgram;

  // Re-validate form when program data changes
  // This ensures validation rules are applied when program settings are loaded
  useEffect(() => {
    if (currentProgram) {
      // Re-validate all fields when program data changes
      trigger();
    }
  }, [
    currentProgram?.configuration?.assignCMCoordinator,
    currentProgram?.configuration?.enabledFormFactor2,
    trigger,
  ]);

  useEffect(() => {
    if (!programWatched) {
      setValue("organizationUnit", "");
      setValue("cmCoordinator", "");
      setValue("formFactor1", "");
      setValue("formFactor2", "");
      setValue("naloxoneId", "");
    }
  }, [programWatched, setValue]);

  // Clear formFactor2 if it matches the newly selected formFactor1
  useEffect(() => {
    const formFactor2Value = getValues("formFactor2") as string;
    if (formFactor1Watched && formFactor2Value === formFactor1Watched) {
      setValue("formFactor2", "");
    }
  }, [formFactor1Watched, setValue, getValues]);

  // AC04e: Check if CM Coordinator should be shown
  const assignCMCoordinator = useMemo(
    () => currentProgram?.configuration?.assignCMCoordinator,
    [currentProgram],
  );

  const showCMCoordinator = useMemo(
    () =>
      assignCMCoordinator === AssignCMCoordinatorEnum.OPTIONAL ||
      assignCMCoordinator === AssignCMCoordinatorEnum.MANDATORY,
    [assignCMCoordinator],
  );

  const isCMCoordinatorRequired = useMemo(
    () => assignCMCoordinator === AssignCMCoordinatorEnum.MANDATORY,
    [assignCMCoordinator],
  );

  // AC05e: Check if Form Factor 2 should be shown
  const enabledFormFactor2 = useMemo(
    () => currentProgram?.configuration?.enabledFormFactor2,
    [currentProgram],
  );

  // AC10: Get form factors from program
  const formFactors = useMemo(
    () => currentProgram?.configuration?.formFactors || [],
    [currentProgram],
  );

  const formFactorOptions = useMemo(() => {
    return formFactors.map((factor) => ({
      key: factor.type,
      value: factor.name,
    }));
  }, [formFactors]);

  // Form Factor 1 options: all available options
  const formFactor1Options = useMemo(() => {
    return formFactorOptions;
  }, [formFactorOptions]);

  // Form Factor 2 options: exclude the one selected in Form Factor 1
  const formFactor2Options = useMemo(() => {
    if (!formFactor1Watched) return formFactorOptions;
    return formFactorOptions.filter(
      (option) => option.key !== formFactor1Watched,
    );
  }, [formFactorOptions, formFactor1Watched]);

  useEffect(() => {
    if (program) {
      setValue("assignCMCoordinator", assignCMCoordinator || "");
      setValue("enabledFormFactor2", enabledFormFactor2 || false);
    }
  }, [program, assignCMCoordinator, enabledFormFactor2, setValue]);

  // AC04e: Load CM Coordinators (already filtered by backend to exclude Sysadmin and Support)
  const { cmCoordinatorUsers, isFetchingCmCoordinatorUsers } =
    useGetCmCoordinator(
      {
        programId: programWatched,
        orgUnitId: organizationUnitWatched,
      },
      !!programWatched &&
        !!organizationUnitWatched &&
        showCMCoordinator === true,
    );

  const programs = useMemo(() => {
    if (!programOrgUnits) return [];

    return programOrgUnits?.map((programOrgUnit) => {
      return {
        key: programOrgUnit.programId,
        value: programOrgUnit.programName,
      };
    });
  }, [programOrgUnits]);

  const organizationUnits = useMemo(() => {
    if (!programOrgUnits || !programWatched) return [];

    return (
      programOrgUnits.find((it) => it.programId === programWatched)?.orgUnits ||
      []
    ).map((it) => {
      return {
        key: it.orgUnitId,
        value: it.orgUnitName,
      };
    });
  }, [programOrgUnits, programWatched]);

  const cmCoordinatorUserOptions = useMemo(() => {
    if (!cmCoordinatorUsers) return [];
    return cmCoordinatorUsers.map((it) => ({
      key: it._id as string,
      value: `${it.firstName} ${it.lastName}`,
    }));
  }, [cmCoordinatorUsers, programWatched, organizationUnitWatched]);

  const onSubmit = async (data: EnrollProgramFormData) => {
    // Manual validation for CM Coordinator when mandatory
    if (isCMCoordinatorRequired && !data.cmCoordinator) {
      showSnackBar(tCommon("validations.required"), "error");
      return;
    }

    const payload: any = {
      programId: data.program as string,
      orgUnitId: data.organizationUnit as string,
      cmCoordinator: (data.cmCoordinator as string) || null,
      formFactor1: (data.formFactor1 as string) || null,
      formFactor2: (data.formFactor2 as string) || null,
      naloxoneId: (data.naloxoneId as string) || null,
      updatedBy: userInfo?._id as string,
    };

    await updateEnrollment.mutateAsync(
      {
        accountId: process.env.REACT_APP_ACCOUNT_ID as string,
        participantId: participantId as string,
        enrollmentId: enrollment?._id as string,
        enrollmentData: payload,
      },
      {
        onSuccess: () => {
          onClose();
          showSnackBar(t("client_profile.update-success"), "success");

          const participantName = `${participantResponse?.participant.firstName} ${participantResponse?.participant.lastName}`;
          // AC00-A: Audit log when enrollment is edited
          createAuditLog.mutate({
            appType: "WEB_BACK_OFFICE",
            module: MODULE_NAME.ENROLLMENTS,
            option: OPTIONS.ENROLLMENT,
            actionCode: ACTION_CODE.WEB_PART_ENROLL_EDITED,
            action: "When saved",
            detail: `User has edited enrollment to participant ${participantName}`,
            transactionDate: new Date(),
            accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
            createdBy: userInfo?._id || "",
            name: `${userInfo?.firstName} ${userInfo?.lastName}`,
          });
        },
        onError: (error: ErrorResponse) => {
          const message = Array.isArray(error.message)
            ? error.message[0]
            : error.message;
          showSnackBar(message || t("client_profile.update-error"), "error");
        },
      },
    );
  };

  // AC00-B: Show PermissionDeniedModal if no permission
  if (!canEditEnrollment) {
    return <PermissionDeniedModal open={open} onClose={onClose} />;
  }

  // AC00-D: Show skeletons while loading
  const isLoading =
    isFetchingProgramOrgUnits ||
    isFetchingProgram ||
    isFetchingProgramFromWatch;

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        isPending={false}
        title={t("client_profile.edit_enrollment")}
        theme={theme}
        size={{
          width: "100%",
          minHeight: 440,
          maxHeight: 900,
          maxWidth: 320,
        }}
        content={
          isLoading ? (
            <Grid container spacing={2} mt={2}>
              <Grid size={{ xs: 12 }}>
                <Skeleton variant="rectangular" height={56} />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Skeleton variant="rectangular" height={56} />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Skeleton variant="rectangular" height={56} />
              </Grid>
            </Grid>
          ) : (
            <Grid container spacing={2} mt={2}>
              {/* AC01e/AC02e: Program disabled on edit */}
              <Grid size={{ xs: 12 }}>
                <Controller
                  name="program"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <Select
                      handleChange={(value) => {
                        field.onChange(value);
                        setValue("organizationUnit", "");
                        setValue("cmCoordinator", "");
                      }}
                      isLoading={isFetchingProgramOrgUnits}
                      disabled={true}
                      data={programs}
                      value={(field.value as string) || ""}
                      label={t("client_profile.program")}
                      id="program"
                      fullWidth
                      error={!!errors.program}
                      errorMessage={errors.program?.message}
                      required={true}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Controller
                  name="organizationUnit"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <Select
                      handleChange={(value) => {
                        field.onChange(value);
                        setValue("cmCoordinator", "");
                      }}
                      isLoading={isFetchingProgramOrgUnits}
                      disabled={!programWatched}
                      data={organizationUnits}
                      value={(field.value as string) || ""}
                      label={t("client_profile.organization_unit")}
                      id="organizationUnit"
                      fullWidth
                      error={!!errors.organizationUnit}
                      errorMessage={errors.organizationUnit?.message}
                      required={true}
                    />
                  )}
                />
              </Grid>
              {/* AC04e: CM Coordinator - conditional based on assignCMCoordinator */}
              {showCMCoordinator && (
                <Grid size={{ xs: 12 }}>
                  <Controller
                    name="cmCoordinator"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <Select
                        handleChange={(value) => field.onChange(value)}
                        isLoading={isFetchingCmCoordinatorUsers}
                        disabled={!programWatched || !organizationUnitWatched}
                        data={cmCoordinatorUserOptions}
                        value={(field.value as string) || ""}
                        label={t("client_profile.cm_coordinator")}
                        id="cmCoordinator"
                        fullWidth
                        error={!!errors.cmCoordinator}
                        errorMessage={errors.cmCoordinator?.message}
                        required={isCMCoordinatorRequired}
                      />
                    )}
                  />
                </Grid>
              )}

              {/* AC10: Form Factor 1 */}
              {formFactor1Options.length > 0 && (
                <Grid size={{ xs: 12 }}>
                  <Controller
                    name="formFactor1"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <Select
                        handleChange={(value) => field.onChange(value)}
                        isLoading={false}
                        disabled={!programWatched}
                        data={formFactor1Options}
                        value={(field.value as string) || ""}
                        label="Form factor 1"
                        id="formFactor1"
                        fullWidth
                        error={!!errors.formFactor1}
                        errorMessage={errors.formFactor1?.message}
                        required={false}
                      />
                    )}
                  />
                </Grid>
              )}

              {/* AC05e: Form Factor 2 - conditional based on enabledFormFactor2 */}
              {enabledFormFactor2 && formFactor2Options.length > 0 && (
                <Grid size={{ xs: 12 }}>
                  <Controller
                    name="formFactor2"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <Select
                        handleChange={(value) => field.onChange(value)}
                        isLoading={false}
                        disabled={!programWatched}
                        data={formFactor2Options}
                        value={(field.value as string) || ""}
                        label="Form factor 2"
                        id="formFactor2"
                        fullWidth
                        error={!!errors.formFactor2}
                        errorMessage={errors.formFactor2?.message}
                        required={false}
                      />
                    )}
                  />
                </Grid>
              )}

              {/* AC11: Naloxone ID */}
              <Grid size={{ xs: 12 }}>
                <Controller
                  name="naloxoneId"
                  control={control}
                  defaultValue=""
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Naloxone ID"
                      id="naloxoneId"
                      fullWidth
                      error={!!errors.naloxoneId}
                      helperText={errors.naloxoneId?.message}
                      inputProps={{
                        maxLength: 20,
                      }}
                    />
                  )}
                />
              </Grid>
            </Grid>
          )
        }
        actions={
          <Box
            sx={{
              display: "flex",
              gap: "10px",
            }}
          >
            <Button
              variantType="cancel"
              onClick={onClose}
              disabled={false}
              label={tCommon("cancel")}
              sx={{ textTransform: "uppercase" }}
            />
            <Button
              variantType="save"
              onClick={async () => {
                const isValid = await trigger();
                if (isValid) {
                  handleSubmit(onSubmit)();
                } else {
                  showSnackBar(tCommon("validations.form-with-errors"));
                }
              }}
              disabled={updateEnrollment.isPending}
              isLoading={updateEnrollment.isPending}
              label={t("client_profile.save")}
              sx={{ textTransform: "uppercase" }}
            />
          </Box>
        }
      />
    </>
  );
};

export default ParticipantEditProgramModal;
